import * as React from 'react';

import type { DragEndEvent } from '@dnd-kit/core';
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { isAdminOrganization } from '@rematter/common/identity';
import { Button, Tooltip, cn } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  LucideChevronDown,
  LucideGripVertical,
  LucideHome,
  LucideLayoutList,
} from 'lucide-react';

import { patchScopedSettingValuesMutationAtom } from '../../_graphql/mutationPatchScopedSettingValues';
import { useTranslations } from '../../i18n/useTranslations';
import { FormSection } from '../_shared/FormSection';

import {
  appSidebarModules,
  filterModulesByEntitlements,
} from '~/app/AppRouter';
import { useIdentity } from '~/shared/hooks/useIdentity';
import { useTranslations as useSharedTranslations } from '~/shared/i18n/useTranslations';
import { useEntitlements } from '~/shared/providers/auth';
import type { NavItem } from '~/shared/routing/createModuleNav';
import type { ModuleDefinition } from '~/shared/routing/types';
import {
  $allSettingsGroups,
  $defaultLandingModule,
  $userSidebarModuleOrder,
  $userSidebarSubsectionOrder,
} from '~/shared/state/settings.state';
import {
  sortModulesByOrder,
  sortSubsectionsByOrder,
} from '~/shared/utils/sortNavItems';

type SortableModuleItemProps = {
  module: ModuleDefinition;
  isDefaultLanding: boolean;
  settingsLoading: boolean;
  hideDefaultLanding: boolean;
  isDisabled: boolean;
  isExpanded?: boolean;
  hasSubsections?: boolean;
  onSetDefaultLanding: (modulePath: string) => void;
  onToggleExpand?: () => void;
};

//
// ⚛️ ⚛️ ⚛️ SORTABLE MODULE ITEM ⚛️ ⚛️ ⚛️
//
const SortableModuleItem = ({
  module,
  isDefaultLanding,
  settingsLoading,
  hideDefaultLanding,
  isDisabled,
  isExpanded,
  hasSubsections,
  onSetDefaultLanding,
  onToggleExpand,
}: SortableModuleItemProps) => {
  const t = useSharedTranslations();
  const settingsT = useTranslations();

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: module.path,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const Icon = module.icon;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        '@SortableModuleItem',
        'flex items-center gap-2 rounded-md border border-neutral/15 bg-white px-2 py-1.5',
        isDragging && 'opacity-50',
      )}
    >
      <div
        className="flex cursor-move items-center text-neutral/40"
        {...attributes}
        {...listeners}
      >
        <LucideGripVertical className="size-4" />
      </div>
      {Icon && <Icon className="size-4 text-neutral/60" />}
      <span className="flex-1 text-sm font-medium">{module.label(t)}</span>
      {hasSubsections && (
        <Button
          variant="ghost"
          size="icon"
          onPress={onToggleExpand}
          icon={
            <LucideChevronDown
              className={cn(
                'size-4 transition-transform duration-200',
                isExpanded && 'rotate-180',
              )}
            />
          }
        />
      )}
      {settingsLoading || hideDefaultLanding ? (
        <div className="size-4 shrink-0" />
      ) : (
        <Tooltip
          tip={
            isDefaultLanding
              ? settingsT.preferencesView.moduleOrder.defaultLandingPage
              : settingsT.preferencesView.moduleOrder.setAsLandingPage
          }
          wrapWithButton={false}
        >
          <Button
            variant="text"
            size="small"
            onPress={() => onSetDefaultLanding(module.path)}
            isDisabled={isDisabled}
            color={isDefaultLanding ? 'primary' : 'neutral'}
            className={cn(isDefaultLanding && 'bg-primary/5')}
            icon={
              <LucideHome
                className={cn(
                  'size-4',
                  !isDefaultLanding && 'text-neutral/30 hover:text-neutral/50',
                )}
              />
            }
            aria-label={
              isDefaultLanding
                ? settingsT.preferencesView.moduleOrder.defaultLandingPage
                : settingsT.preferencesView.moduleOrder.setAsLandingPage
            }
          />
        </Tooltip>
      )}
    </div>
  );
};

type SortableSubsectionItemProps = {
  item: NavItem;
  isDefaultLanding: boolean;
  settingsLoading: boolean;
  isDisabled: boolean;
  onSetDefaultLanding: (path: string) => void;
};

//
// ⚛️ ⚛️ ⚛️ SORTABLE SUBSECTION ITEM ⚛️ ⚛️ ⚛️
//
const SortableSubsectionItem = ({
  item,
  isDefaultLanding,
  settingsLoading,
  isDisabled,
  onSetDefaultLanding,
}: SortableSubsectionItemProps) => {
  const t = useSharedTranslations();
  const settingsT = useTranslations();

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: item.route.path,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const Icon = item.route.icon;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        '@SortableSubsectionItem',
        'ml-6 flex items-center gap-2 rounded-md border border-neutral/10 bg-neutral/3 px-2 py-1.5',
        isDragging && 'opacity-50',
      )}
    >
      <div
        className="flex cursor-move items-center text-neutral/40"
        {...attributes}
        {...listeners}
      >
        <LucideGripVertical className="size-4" />
      </div>
      {Icon && <Icon className="size-4 text-neutral/50" />}
      <span className="flex-1 text-sm">{item.route.label?.(t)}</span>
      {settingsLoading ? (
        <div className="size-4 shrink-0" />
      ) : (
        <Tooltip
          tip={
            isDefaultLanding
              ? settingsT.preferencesView.moduleOrder.defaultLandingPage
              : settingsT.preferencesView.moduleOrder.setAsLandingPage
          }
          wrapWithButton={false}
        >
          <Button
            variant="text"
            size="small"
            onPress={() => onSetDefaultLanding(item.route.path)}
            isDisabled={isDisabled}
            color={isDefaultLanding ? 'primary' : 'neutral'}
            className={cn(isDefaultLanding && 'bg-primary/5')}
            icon={
              <LucideHome
                className={cn(
                  'size-4',
                  !isDefaultLanding && 'text-neutral/30 hover:text-neutral/50',
                )}
              />
            }
            aria-label={
              isDefaultLanding
                ? settingsT.preferencesView.moduleOrder.defaultLandingPage
                : settingsT.preferencesView.moduleOrder.setAsLandingPage
            }
          />
        </Tooltip>
      )}
    </div>
  );
};

const $patchModuleOrder = patchScopedSettingValuesMutationAtom({
  onSuccess: (get) => {
    get($allSettingsGroups).refetch();
  },
});

const $patchDefaultLanding = patchScopedSettingValuesMutationAtom({
  onSuccess: (get) => {
    get($allSettingsGroups).refetch();
  },
});

const $patchSubsectionOrder = patchScopedSettingValuesMutationAtom({
  onSuccess: (get) => {
    get($allSettingsGroups).refetch();
  },
});

const getVisibleSubsections = (module: ModuleDefinition): NavItem[] =>
  module.nav?.children.filter((item) => item.route.label) ?? [];

type SubsectionListProps = {
  parentPath: string;
  items: NavItem[];
  defaultLanding: string | null;
  settingsLoading: boolean;
  isSavingLanding: boolean;
  onSetDefaultLanding: (path: string) => void;
  onDragEnd: (parentPath: string, event: DragEndEvent) => void;
  sensors: ReturnType<typeof useSensors>;
};

//
// ⚛️ ⚛️ ⚛️ SUBSECTION LIST ⚛️ ⚛️ ⚛️
//
const SubsectionList = ({
  parentPath,
  items,
  defaultLanding,
  settingsLoading,
  isSavingLanding,
  onSetDefaultLanding,
  onDragEnd,
  sensors,
}: SubsectionListProps) => {
  const handleDragEnd = React.useCallback(
    (event: DragEndEvent) => onDragEnd(parentPath, event),
    [parentPath, onDragEnd],
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={items.map((i) => i.route.path)}
        strategy={verticalListSortingStrategy}
      >
        <div className="flex flex-col gap-1">
          {items.map((item) => (
            <SortableSubsectionItem
              key={item.route.path}
              item={item}
              isDefaultLanding={item.route.path === defaultLanding}
              settingsLoading={settingsLoading}
              isDisabled={isSavingLanding}
              onSetDefaultLanding={onSetDefaultLanding}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};

//
// ⚛️ ⚛️ ⚛️ SIDEBAR MODULE ORDER SECTION ⚛️ ⚛️ ⚛️
//
export const SidebarModuleOrderSection = () => {
  const t = useTranslations();
  const sharedT = useSharedTranslations();

  const [isSavingLanding, setIsSavingLanding] = React.useState(false);
  const [collapsedModules, setCollapsedModules] = React.useState<Set<string>>(
    new Set(),
  );

  const moduleOrder = useAtomValue($userSidebarModuleOrder);
  const subsectionOrder = useAtomValue($userSidebarSubsectionOrder);
  const defaultLanding = useAtomValue($defaultLandingModule);
  const { isPending: settingsLoading } = useAtomValue($allSettingsGroups);
  const patchModuleOrder = useSetAtom($patchModuleOrder);
  const patchDefaultLanding = useSetAtom($patchDefaultLanding);
  const patchSubsectionOrder = useSetAtom($patchSubsectionOrder);

  const toast = useToast();
  const { entitlements } = useEntitlements();
  const { user, organization } = useIdentity();
  const isAdminOrg = isAdminOrganization(organization);
  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor),
  );

  const availableModules = React.useMemo(
    () => filterModulesByEntitlements(appSidebarModules, entitlements),
    [entitlements],
  );

  const serverOrderedModules = React.useMemo(
    () => sortModulesByOrder(availableModules, moduleOrder),
    [availableModules, moduleOrder],
  );

  const [localModules, setLocalModules] =
    React.useState<ModuleDefinition[]>(serverOrderedModules);

  const [localSubsectionOrder, setLocalSubsectionOrder] = React.useState<
    Record<string, NavItem[]>
  >({});

  const orderedSubsections = React.useMemo(() => {
    const result: Record<string, NavItem[]> = {};
    for (const module of localModules) {
      const visible = getVisibleSubsections(module);
      if (visible.length > 0) {
        const savedOrder = subsectionOrder[module.path];
        result[module.path] =
          savedOrder && savedOrder.length > 0
            ? sortSubsectionsByOrder(visible, savedOrder)
            : visible;
      }
    }
    return result;
  }, [localModules, subsectionOrder]);

  React.useEffect(() => {
    setLocalModules(serverOrderedModules);
  }, [serverOrderedModules]);

  React.useEffect(() => {
    setLocalSubsectionOrder(orderedSubsections);
  }, [orderedSubsections]);

  const handleModuleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !user) return;

    const oldIndex = localModules.findIndex((m) => m.path === active.id);
    const newIndex = localModules.findIndex((m) => m.path === over.id);
    const reordered = arrayMove(localModules, oldIndex, newIndex);

    setLocalModules(reordered);

    patchModuleOrder({
      scope: 'User',
      objectId: user.id,
      values: {
        'navigation.sidebarModuleOrder': reordered.map((m) => m.path),
      },
    }).catch(() => {
      setLocalModules(serverOrderedModules);
      toast.error(t.preferencesView.moduleOrder.messages.saveError);
    });
  };

  const handleSubsectionDragEnd = React.useCallback(
    (parentPath: string, event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id || !user) return;

      const items = localSubsectionOrder[parentPath];
      if (!items) return;

      const oldIndex = items.findIndex((i) => i.route.path === active.id);
      const newIndex = items.findIndex((i) => i.route.path === over.id);
      const reordered = arrayMove(items, oldIndex, newIndex);

      setLocalSubsectionOrder((prev) => ({
        ...prev,
        [parentPath]: reordered,
      }));

      const newOrder = {
        ...subsectionOrder,
        [parentPath]: reordered.map((i) => i.route.path),
      };

      patchSubsectionOrder({
        scope: 'User',
        objectId: user.id,
        values: { 'navigation.sidebarSubsectionOrder': newOrder },
      }).catch(() => {
        setLocalSubsectionOrder(orderedSubsections);
        toast.error(t.preferencesView.moduleOrder.messages.saveError);
      });
    },
    [
      user,
      localSubsectionOrder,
      subsectionOrder,
      orderedSubsections,
      patchSubsectionOrder,
      toast,
      t,
    ],
  );

  const handleReset = () => {
    if (!user) return;

    setLocalModules(availableModules);
    setLocalSubsectionOrder({});

    patchModuleOrder({
      scope: 'User',
      objectId: user.id,
      values: {
        'navigation.sidebarModuleOrder': [],
        'navigation.sidebarSubsectionOrder': {},
      },
    }).catch(() => {
      setLocalModules(serverOrderedModules);
      setLocalSubsectionOrder(orderedSubsections);
      toast.error(t.preferencesView.moduleOrder.messages.saveError);
    });
  };

  const handleSetDefaultLanding = (path: string) => {
    if (!user) return;

    const newValue = path === defaultLanding ? '' : path;

    const allModules = localModules.flatMap((m) => {
      const subs = localSubsectionOrder[m.path];
      return subs
        ? subs.map((s) => ({
            path: s.route.path,
            label: (t: typeof sharedT) => s.route.label?.(t) ?? '',
          }))
        : [{ path: m.path, label: m.label }];
    });
    const match = allModules.find((m) => m.path === path);

    setIsSavingLanding(true);
    patchDefaultLanding({
      scope: 'User',
      objectId: user.id,
      values: { 'navigation.defaultLandingModule': newValue },
    })
      .then(() => {
        if (newValue && match) {
          toast.success(
            t.preferencesView.moduleOrder.landingPageSet(match.label(sharedT)),
          );
        } else {
          toast.success(t.preferencesView.moduleOrder.landingPageCleared);
        }
      })
      .catch(() => {
        toast.error(t.preferencesView.moduleOrder.messages.landingPageError);
      })
      .finally(() => {
        setIsSavingLanding(false);
      });
  };

  const toggleModuleExpanded = React.useCallback((modulePath: string) => {
    setCollapsedModules((prev) => {
      const next = new Set(prev);
      if (next.has(modulePath)) {
        next.delete(modulePath);
      } else {
        next.add(modulePath);
      }
      return next;
    });
  }, []);

  const isCustomOrder =
    moduleOrder.length > 0 || Object.keys(subsectionOrder).length > 0;

  return (
    <FormSection
      icon={LucideLayoutList}
      title={t.preferencesView.moduleOrder.title}
      className="@SidebarModuleOrderSection"
    >
      <div className="flex flex-col gap-3">
        <p className="text-sm text-neutral/60">
          {t.preferencesView.moduleOrder.description}
        </p>
        <div className="flex w-[260px] flex-col gap-2">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleModuleDragEnd}
          >
            <SortableContext
              items={localModules.map((m) => m.path)}
              strategy={verticalListSortingStrategy}
            >
              <div className="flex flex-col gap-1">
                {localModules.map((module) => {
                  const subsections = localSubsectionOrder[module.path];
                  const hasSubsections = subsections && subsections.length > 0;
                  const isExpanded =
                    hasSubsections && !collapsedModules.has(module.path);

                  return (
                    <div key={module.path} className="flex flex-col gap-1">
                      <SortableModuleItem
                        module={module}
                        isDefaultLanding={module.path === defaultLanding}
                        settingsLoading={settingsLoading}
                        hideDefaultLanding={isAdminOrg}
                        isDisabled={isSavingLanding}
                        hasSubsections={!!hasSubsections}
                        isExpanded={isExpanded}
                        onSetDefaultLanding={handleSetDefaultLanding}
                        onToggleExpand={() => toggleModuleExpanded(module.path)}
                      />
                      {isExpanded && (
                        <SubsectionList
                          parentPath={module.path}
                          items={subsections}
                          defaultLanding={defaultLanding}
                          settingsLoading={settingsLoading}
                          isSavingLanding={isSavingLanding}
                          onSetDefaultLanding={handleSetDefaultLanding}
                          onDragEnd={handleSubsectionDragEnd}
                          sensors={sensors}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </SortableContext>
          </DndContext>
          {isCustomOrder && (
            <Button
              variant="outline"
              size="small"
              className="mt-4"
              onPress={handleReset}
            >
              {t.preferencesView.moduleOrder.resetToDefault}
            </Button>
          )}
        </div>
      </div>
    </FormSection>
  );
};
